/**
 * #650 Discard Worker: the owner's word for the existing guarded `retire_worker` RPC.
 *
 * There is no second retirement/delete path here. The database stays the authority: it
 * refuses a busy Worker, one named by waiting/blocked work, or one named by a standing
 * routing scope, parks idle sessions offline, marks `workers.retired_at`, and keeps the
 * Worker number and every historical run/task/audit row. This module only decides when
 * the button is offered, sends that one request behind the owner's AAL2 guard, and says
 * why a refusal happened in the owner's words.
 */
import type { RunningTask, WorkerRoutingRow } from './data';
import type { WorkerChatReadiness } from './worker-chat-readiness';

/**
 * Readiness reasons that mean the Worker's terminal or AI session has ended. An idle
 * Worker whose AI session has not started yet, or whose AI context was retired between
 * jobs, is healthy and is not offered Discard.
 */
const ENDED_REASONS = new Set<WorkerChatReadiness['reason']>([
  'terminal_ended',
  'no_live_terminal',
  'ai_session_closed',
]);

const WORKING_STATUSES = new Set(['busy', 'rotating', 'awaiting_approval', 'blocked']);

export interface DiscardOffer {
  readonly offered: boolean;
  /** The computer whose seat the Worker leaves; retire_worker needs it. */
  readonly nodeId: string | null;
}

/**
 * Offer Discard only for a Worker that is not working and whose terminal/session has
 * ended. This is presentation only: the server refuses anyway if the UI is stale.
 */
export function discardOffer(
  readiness: WorkerChatReadiness | null,
  input: { readonly running: readonly RunningTask[] },
): DiscardOffer {
  if (readiness === null || readiness.nodeId === null) return { offered: false, nodeId: null };
  const working = readiness.activeTaskId !== null
    || WORKING_STATUSES.has(readiness.workerStatus)
    || input.running.some((run) => run.worker_number === readiness.workerNumber);
  if (working || !ENDED_REASONS.has(readiness.reason)) return { offered: false, nodeId: readiness.nodeId };
  return { offered: true, nodeId: readiness.nodeId };
}

/**
 * The owner-facing sentence for a refusal. retire_worker answers `conflict` for three
 * different reasons; the rows the room already holds say which one applies when they
 * can, and otherwise all three are named rather than guessing one.
 */
export function discardErrorMessage(
  code: string,
  context: {
    readonly workerNumber: number;
    readonly running: readonly RunningTask[];
    readonly routing: readonly WorkerRoutingRow[];
  },
): string {
  const name = `Worker ${context.workerNumber}`;
  switch (code) {
    case 'conflict': {
      const run = context.running.find((item) => item.worker_number === context.workerNumber);
      if (run) return `${name} is still working on "${run.title}". Stop or finish that run, then discard it.`;
      const scopes = context.routing.filter((item) => item.worker_number === context.workerNumber);
      if (scopes.length > 0) {
        const projects = scopes.map((item) => item.project_name).join(', ');
        return `${name} is the routing lock for ${projects}. Clear that routing lock, then discard it.`;
      }
      return `Brunel kept ${name}: it is busy, still named by waiting or blocked work, or named by a routing lock. Finish or reassign that work, then discard it.`;
    }
    case 'forbidden': return 'Confirm it is you (two-step verification) to discard a Worker.';
    case 'not_found': return `${name} has no session on that computer any more. Refresh and try again.`;
    case 'unauthorized': return 'Your session has expired. Sign in again to discard a Worker.';
    case 'invalid_input': return `${name} could not be identified. Refresh and try again.`;
    default: return `${name} was not discarded.`;
  }
}

export interface DiscardResult {
  readonly ok: boolean;
  readonly message: string;
}

/**
 * Send exactly the keys retire_worker accepts, behind the owner's AAL2 guard. The guard
 * may run the action only after step-up, so the outcome is delivered to onResult.
 */
export async function discardWorker(input: {
  readonly orgId: string;
  readonly workerId: string;
  readonly workerNumber: number;
  readonly nodeId: string;
  readonly running: readonly RunningTask[];
  readonly routing: readonly WorkerRoutingRow[];
  readonly guard: (name: 'post_owner_command', action: () => Promise<void>) => Promise<void>;
  readonly rpc: (name: 'retire_worker', args: { readonly request: unknown }) => PromiseLike<{ data: unknown; error: unknown }>;
  readonly refresh: () => Promise<void>;
  readonly onResult: (result: DiscardResult) => void;
}): Promise<void> {
  // retire_worker requires owner aal2; the guard asks for step-up first when needed.
  await input.guard('post_owner_command', async () => {
    let data: unknown = null;
    let error: unknown = null;
    try {
      ({ data, error } = await input.rpc('retire_worker', {
        request: { orgId: input.orgId, workerId: input.workerId, nodeId: input.nodeId },
      }));
    } catch (thrown) {
      error = thrown;
    }
    const result = data as { ok?: boolean; error?: { code?: string } } | null;
    if (error || !result?.ok) {
      input.onResult({ ok: false, message: discardErrorMessage(result?.error?.code ?? '', input) });
      return;
    }
    input.onResult({ ok: true, message: `Worker ${input.workerNumber} discarded. Its history is kept.` });
    // Targeted refresh: the room re-reads workers (retired_at) and the card leaves active surfaces.
    await input.refresh();
  });
}
